/**
 * Descarga progresiva (D30, §6.5): 4 en paralelo, cada imagen apenas está lista.
 * Adaptación automática al entorno, sin preguntarle nada al usuario:
 *  - Chrome/Edge escritorio → showDirectoryPicker (carpeta una vez, sin prompts)
 *  - Firefox/Safari escritorio → <a download> progresivo
 *  - Móvil → navigator.share({files}) por tandas (único camino real en iOS)
 */

export type EstrategiaDescarga = 'carpeta' | 'archivos' | 'compartir';

interface PickerDirectorio {
  getFileHandle: (nombre: string, opciones: { create: boolean }) => Promise<{
    createWritable: () => Promise<{ write: (b: Blob) => Promise<void>; close: () => Promise<void> }>;
  }>;
}

export function esMovil(): boolean {
  return /android|iphone|ipad|ipod/i.test(navigator.userAgent);
}

export function estrategiaDisponible(): EstrategiaDescarga {
  if (esMovil() && typeof navigator.share === 'function' && typeof navigator.canShare === 'function') {
    return 'compartir';
  }
  if ('showDirectoryPicker' in window) return 'carpeta';
  return 'archivos';
}

let carpetaElegida: PickerDirectorio | null = null;

/** Pide la carpeta UNA vez; las siguientes imágenes se escriben sin prompt. */
export async function elegirCarpeta(): Promise<boolean> {
  try {
    const picker = (window as unknown as { showDirectoryPicker: (o: object) => Promise<PickerDirectorio> })
      .showDirectoryPicker;
    carpetaElegida = await picker({ mode: 'readwrite', startIn: 'downloads' });
    return true;
  } catch {
    carpetaElegida = null;
    return false;
  }
}

export async function guardarEnCarpeta(nombre: string, blob: Blob): Promise<boolean> {
  if (!carpetaElegida) return false;
  const handle = await carpetaElegida.getFileHandle(nombre, { create: true });
  const escritura = await handle.createWritable();
  await escritura.write(blob);
  await escritura.close();
  return true;
}

export function descargarArchivo(nombre: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

/** Compartir por tandas en móvil: Fotos o WhatsApp directo (§6.5). */
export async function compartirArchivos(archivos: File[], texto?: string): Promise<boolean> {
  if (typeof navigator.share !== 'function') return false;
  try {
    if (navigator.canShare && !navigator.canShare({ files: archivos })) return false;
    await navigator.share({ files: archivos, text: texto });
    return true;
  } catch {
    return false; // el usuario canceló
  }
}

/** ZIP secundario para lotes grandes en escritorio (D39). */
export async function armarZip(entradas: { nombre: string; datos: Uint8Array }[]): Promise<Blob> {
  const { zipSync } = await import('fflate');
  const mapa: Record<string, Uint8Array> = {};
  for (const e of entradas) mapa[e.nombre] = e.datos;
  const zip = zipSync(mapa, { level: 0 }); // PNG ya está comprimido
  const copia = new Uint8Array(zip); // ArrayBuffer propio para el Blob
  return new Blob([copia], { type: 'application/zip' });
}

/** WhatsApp de escritorio: descarga + wa.me con el texto (§6.5). */
export function abrirWhatsApp(texto: string): void {
  window.open(`https://wa.me/?text=${encodeURIComponent(texto)}`, '_blank', 'noopener');
}

/** Copia el PNG al portapapeles: pegar con Cmd/Ctrl+V lo adjunta en WhatsApp Web y demás redes. */
export async function copiarImagen(blob: Blob): Promise<boolean> {
  try {
    const Item = (window as unknown as { ClipboardItem?: new (d: Record<string, Blob>) => ClipboardItem }).ClipboardItem;
    if (!Item || !navigator.clipboard?.write) return false;
    await navigator.clipboard.write([new Item({ 'image/png': blob })]);
    return true;
  } catch {
    return false;
  }
}
